import {
  networkLabelForStatementDescriptor,
  statementDescriptorMaxLength,
  type StatementDescriptorValidationResult,
  validateStatementDescriptor
} from '@klear/bank-account/statement-descriptor'
import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE, type BankPaymentNetwork } from '@klear/bank-account/constants'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'

type PaymentReferenceInputProps = {
  network: BankPaymentNetwork | ''
  value: string
  onChange: (value: string) => void
  error?: string
  id?: string
}

function resolveNetwork(network: BankPaymentNetwork | ''): BankPaymentNetwork {
  return network === PAYMENT_NETWORK_WIRE ? PAYMENT_NETWORK_WIRE : PAYMENT_NETWORK_ACH
}

export function validatePaymentReferenceInput(
  value: string,
  network: BankPaymentNetwork | ''
): StatementDescriptorValidationResult {
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return { ok: true }
  }
  return validateStatementDescriptor(trimmed, resolveNetwork(network))
}

export function PaymentReferenceInput({ network, value, onChange, error, id }: PaymentReferenceInputProps) {
  const resolvedNetwork = resolveNetwork(network)
  const maxLength = statementDescriptorMaxLength(resolvedNetwork)
  const networkLabel = networkLabelForStatementDescriptor(resolvedNetwork)
  const used = value.length
  const atCapacity = used >= maxLength

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium">
        Payment reference
      </Label>
      <Input
        id={id}
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Optional reference for the recipient's bank statement"
        aria-invalid={error != null}
      />
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Appears on the recipient&apos;s bank statement. Max {maxLength} characters for {networkLabel}.
        </p>
        <p
          className={cn('shrink-0 text-xs tabular-nums', atCapacity ? 'text-destructive' : 'text-muted-foreground')}
          aria-live="polite"
        >
          {used}/{maxLength}
        </p>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
